// auth.js

// Global variable to cache the access token and its expiration time
let accessTokenCache = {
    token: null,
    expiresAt: 0,
    // Add a flag to prevent multiple threads from trying to refresh the token simultaneously
    isRefreshing: false
};

// Production Client ID and Client Secret
const CLIENT_ID = "id-bb4aeb8a-e476-5cfe-3346-4e0bde719d6";
const CLIENT_SECRET = "secret-5a51426a-2f44-66ae-cc3e-b93d43ce05f";

/**
 * Fetches the access token, using the cached token if valid.
 * Refreshes the token only if it is expired.
 * @returns {Promise<string>} The valid access token.
 */
export async function getAccessToken() {
    const now = Date.now();
    // Check if the cached token is still valid (give a 60-second buffer)
    if (accessTokenCache.token && accessTokenCache.expiresAt > now + 60000) {
        return accessTokenCache.token;
    }

    // If a refresh is already in progress, wait for it to complete
    if (accessTokenCache.isRefreshing) {
        // Simple polling mechanism to wait for the ongoing refresh
        await new Promise(resolve => setTimeout(resolve, 500));
        // Recurse to check the cache again
        return getAccessToken();
    }

    try {
        accessTokenCache.isRefreshing = true;

        const response = await fetch('/o/oauth2/token', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
                grant_type: 'client_credentials',
                client_id: CLIENT_ID,
                client_secret: CLIENT_SECRET,
            }),
        });

        if (!response.ok) {
            throw new Error(`Failed to get token: ${response.status}`);
        }

        const data = await response.json();

        // Cache the new token and calculate the expiration time
        const tokenExpirationTime = data.expires_in * 1000; // expires_in is usually in seconds
        accessTokenCache.token = data.access_token;
        accessTokenCache.expiresAt = now + tokenExpirationTime;

        return data.access_token;
    } catch (error) {
        console.error('Error fetching access token:', error);
        // Clear token cache on failure
        accessTokenCache.token = null;
        accessTokenCache.expiresAt = 0;
        return null;
    } finally {
        accessTokenCache.isRefreshing = false;
    }
}